import { useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import {
  buildLookupSelectOptions,
  labelCountry,
  labelCurrency,
  labelNameOnly,
  labelVessel,
  labelWithAbrv,
  toLookupList,
  type LookupRecord,
} from './lookupHelpers';
import type { SelectOption } from '../Select';

const CREW_LOOKUPS_KEY = '/crew/lookups';

type CrewLookupsBundle = Record<string, unknown>;

export function useCrewLookupsBundle() {
  return useSWR<CrewLookupsBundle>(CREW_LOOKUPS_KEY, fetcher, { revalidateOnFocus: false });
}

export function useCrewLookupSlice(
  key: string,
  keepIds?: Array<string | number | null | undefined>,
  getLabel: (item: LookupRecord) => string = labelWithAbrv,
): { options: SelectOption[]; isLoading: boolean } {
  const { data, isLoading } = useCrewLookupsBundle();
  const options = useMemo(() => {
    const raw = data?.[key];
    const items = Array.isArray(raw) ? (raw as LookupRecord[]) : [];
    return buildLookupSelectOptions({ items, getLabel, keepIds });
  }, [data, key, keepIds, getLabel]);
  return { options, isLoading: Boolean(isLoading) && !data };
}

/** Fetch a list endpoint; filter active + sort by sortOrder (or custom compare). */
export function useEndpointLookupOptions(opts: {
  endpoint: string | null;
  getLabel: (item: LookupRecord) => string;
  getValue?: (item: LookupRecord) => string;
  keepIds?: Array<string | number | null | undefined>;
  compare?: (a: LookupRecord, b: LookupRecord) => number;
}): { options: SelectOption[]; isLoading: boolean } {
  const { data, isLoading } = useSWR(opts.endpoint, fetcher, { revalidateOnFocus: false });
  const options = useMemo(() => {
    const items = toLookupList(data);
    return buildLookupSelectOptions({
      items,
      getLabel: opts.getLabel,
      getValue: opts.getValue,
      keepIds: opts.keepIds,
      compare: opts.compare,
    });
  }, [data, opts.getLabel, opts.getValue, opts.keepIds, opts.compare]);
  return { options, isLoading: Boolean(isLoading) && !data };
}

export function compareByName(a: LookupRecord, b: LookupRecord): number {
  return labelNameOnly(a).localeCompare(labelNameOnly(b), undefined, { sensitivity: 'base' });
}

export function compareByVesselName(a: LookupRecord, b: LookupRecord): number {
  return labelVessel(a).localeCompare(labelVessel(b), undefined, { sensitivity: 'base' });
}

export function compareByCountryName(a: LookupRecord, b: LookupRecord): number {
  return labelCountry(a).localeCompare(labelCountry(b), undefined, { sensitivity: 'base' });
}

export function compareByCurrency(a: LookupRecord, b: LookupRecord): number {
  const ao = Number(a.sortOrder);
  const bo = Number(b.sortOrder);
  const aOrder = Number.isFinite(ao) ? ao : 999_999;
  const bOrder = Number.isFinite(bo) ? bo : 999_999;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return labelCurrency(a).localeCompare(labelCurrency(b), undefined, { sensitivity: 'base' });
}
